import { useMemo } from "react";

import { type ApolloError } from "@apollo/client";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId } from "../../../gql-generated/types";
import { type LensFolderRoleTypePermissions } from "../../constants";
import { useContainersIndexV2 } from "../useOrganizationsV2";
import { useSelectedLensFolder } from "../useSelectedLensFolder";
import { useSelectedOrganizationAndLensFolderV3 } from "../useSelectedOrganizationAndLensFolderV3";

import { getLensFolderPermissions } from "./getLensFolderPermissions";
import { getLensFolderPermissionsWithContainer } from "./getLensFolderPermissionsV2";
import { useRolesStore } from "./useRolesStore";

export const useSelectedLensFolderPermissions = (): {
    selectedLensFolderPermissions: LensFolderRoleTypePermissions;
    loading: boolean;
    error?: ApolloError;
} => {
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { orgId, lensFolderId } = useSelectedOrganizationAndLensFolderV3();
    const { lensFolder: selectedLensFolder } = useSelectedLensFolder();

    const { rolesStore, loading, error } = useRolesStore();
    const containersIndexV2 = useContainersIndexV2();

    const selectedLensFolderPermissions = useMemo(() => {
        if (isMyLensesOrgsV2Enabled) {
            return getLensFolderPermissionsWithContainer(selectedLensFolder, containersIndexV2);
        }
        return getLensFolderPermissions(orgId ?? "", lensFolderId ?? "", rolesStore);
    }, [isMyLensesOrgsV2Enabled, rolesStore, lensFolderId, orgId, selectedLensFolder, containersIndexV2]);

    return { selectedLensFolderPermissions, loading, error };
};
